import React, { useRef } from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Payment } from 'models/PaymentModel';
import AsyncSelect from 'react-select/async';
import { useSearchParams } from 'react-router-dom';
import { wait } from '@testing-library/user-event/dist/utils';

const PaymentForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.PAYMENT, Code.Form.Name.PAYMENT];
  const {keyId, activeId, setActiveId, loading, setLoading, hasUnsaved, setHasUnsaved, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [insurerOptions, setInsurerOptions] = Hooks.Form.Options();
  const [mopOptions, setMopOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [sequenceOptions, setSequenceOptions] = Hooks.Form.Options();
  const [bankOptions, setBankOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Payment>({});
  const [initialData, setInitialData] = useState<Payment>({});
  const [clientField, setClientField] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get(Code.UrlPara.Client);
  const invoiceId = searchParams.get(Code.UrlPara.Invoice);
  const segmentId = searchParams.get(Code.UrlPara.Segment);
  const insurerId = searchParams.get(Code.UrlPara.Insurer);
  const sequenceId = searchParams.get(Code.UrlPara.Sequence);
  const amountId = searchParams.get(Code.UrlPara.Amount);
  
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.INSURER, Code.Option.Common.NA, setInsurerOptions, setError);
    Files.Json.API(Code.File.SEGMENT, Code.Option.Common.SELECT, setSegmentOptions, setError);
    Files.Json.API(Code.File.MOP, Code.Option.Common.SELECT, setMopOptions, setError);
    Files.Json.API(Code.File.BANK, Code.Option.Common.NA, setBankOptions, setError);
    Files.Json.API(Code.File.CHECK+Code.File.STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.Http(Code.File.SEQUENCE, Code.Option.Common.SELECT, setSequenceOptions, setError);
    
    API.Data.GetByKeyId<Payment>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.payment_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        const finalData = {
          ...formData,
          ...clientData, 
        };
        setFormData(finalData);
        setInitialData(finalData);
        if(clientId) setClientField(true);
        setLoading(false);
      }})

    const onNewEntry = async () => {
      if(keyId===Code.Key.NEW && clientId) {
        const clientName = await API.Data.GetClientName(Number(clientId));
        const newData = {
          client_id: Number(clientId),
          client_name: Utility.Value.Text(clientName), 
          invoice_id: invoiceId ? Number(invoiceId) : undefined,
          segment_id: segmentId ? Number(segmentId) : undefined,
          insurer_id: insurerId ? Number(insurerId) : undefined,
          sequence_id: sequenceId ? Number(sequenceId) : undefined,
          payment_amount: amountId ? Number(amountId) : undefined,
        };
        setFormData(newData);
        setInitialData(newData);
      }
      setLoading(false);
    };
    onNewEntry();
  }, []);
  
  useEffect(() => { 
    const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialData);
    setHasUnsaved(hasChanged); 
  }, [formData, initialData]);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
    });

    //console.log("Final Data to be saved>>>>>", finalData);

    const response = await API.Data.Save(formName, keyId, finalData, setMessage, true, Code.Form.PopupClose.REMAINOPEN)
    if (response) {
      setFormData(finalData);
      setInitialData(finalData);
      setHasUnsaved(false);
      //console.log("Payment>>>response>>>>>", response);
      const newKeyId = response.data?.newid;
      if(newKeyId){
        const url = `custom/payment/verify/${newKeyId}`;
        //console.log("Payment>>>Url>>>>>", url);
        const paymentResponse = await API.Data.callGetApi(url, setMessage);
        //console.log("Payment>>>paymentResponse>>>>>", paymentResponse);
        if (paymentResponse) {
          await Utility.Set.Delay(2000);
        }
        Utility.Button.PopupClose();
      }
      
    }
  };


  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} hasUnsaved={hasUnsaved} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Payment Details"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Date id={Name.Payment.Date} value={formData.payment_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} currentDate={true} />
                <Field.Select id={Name.Common.SegmentId} value={formData.segment_id} title={Title.Segment} options={segmentOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} selected={Code.Option.Segment.OPTICAL} />
              </Control.Row>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" isDisabled={clientField} />
                </Field.Header>
              </Control.Row>
              <Control.Row>
                <Field.FilterSelect id={Name.Insurer.Id} value={formData.insurer_id} title={Title.Insurer} options={insurerOptions} setFormData={setFormData} size={[0,50,1]}  />
                <Field.Text id={Name.Payment.RefNumber} value={formData.payment_ref_number} title={Title.Refno} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Amount id={Name.Payment.Amount} value={formData.payment_amount} title={Title.Amount} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.FilterSelect id={Name.Common.ModeId} value={formData.mode_id} title={Title.MOP} options={mopOptions} setFormData={setFormData} size={[1,50,0]}  require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} />
                <Field.Select id={Name.Bank.Id} value={formData.bank_id} title={Title.Bank} options={bankOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[1,50,0]} />
              </Control.Row>
              <Control.Row>
                <Field.Area id={Name.Payment.Comment} value={formData.payment_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Common.SequenceId} value={formData.sequence_id} title={Title.Source} options={sequenceOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
              </Control.Row>
            </Control.Tab.Content>
            
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default PaymentForm;